import { useQuery } from '@tanstack/react-query';
import type {
  AttachmentDto,
  LocationDto,
  Page,
  PutawayTaskDto,
  ReceiptDetailDto,
  ReceiptLineDto,
  ReceiveResult,
} from '@zenarty/shared';
import {
  ArrowLeft,
  Camera,
  CheckCircle2,
  ClipboardCheck,
  Forklift,
  PackageCheck,
  Printer,
  ScanLine,
  Tag,
  Timer,
  Truck,
  XCircle,
} from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api, fetchObjectUrl, fileToBase64 } from '../api';
import { useAuth } from '../auth';
import { LpnLabelDialog } from '../components/barcode';
import {
  Badge,
  Button,
  Callout,
  Card,
  EmptyState,
  ErrorMessage,
  Field,
  KeyValue,
  PageHeader,
  Skeleton,
  Stat,
} from '../components/kit';
import { ConfirmDialog, Dialog } from '../components/overlays';
import { ReasonSelect } from '../components/pickers';
import { DataTable, type Column } from '../components/table';
import { useToast } from '../components/toast';
import { formatDateTime, formatNumber, timeAgo } from '../lib/format';
import { useAction } from '../lib/queries';
import { minutesLabel, Progress, ReceiptStatus } from './Inbound';

const STEPS: { key: keyof ReceiptDetailDto; label: string }[] = [
  { key: 'createdAt', label: 'Expected' },
  { key: 'arrivedAt', label: 'Checked in' },
  { key: 'startedAt', label: 'Receiving' },
  { key: 'closedAt', label: 'Closed' },
  { key: 'stockedAt', label: 'Stocked' },
];

function Milestones({ r }: { r: ReceiptDetailDto }) {
  return (
    <div className="steps">
      {STEPS.map((s) => {
        const at = r[s.key] as string | null;
        return (
          <div key={s.label} className={`step ${at ? 'done' : ''}`}>
            <span className="step-dot">{at ? <CheckCircle2 aria-hidden /> : null}</span>
            <div>
              <div className="step-label">{s.label}</div>
              <div className="step-time">{at ? formatDateTime(at) : '—'}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ReceiptDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const q = useQuery({
    queryKey: ['receipts', 'detail', id],
    queryFn: () => api<ReceiptDetailDto>(`/receipts/${id}`),
    refetchInterval: 20_000,
  });
  const [checkIn, setCheckIn] = useState(false);
  const [receiveLine, setReceiveLine] = useState<ReceiptLineDto | null>(null);
  const [closing, setClosing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [label, setLabel] = useState<string | null>(null);
  const [putaway, setPutaway] = useState<PutawayTaskDto | null>(null);
  const r = q.data;
  const toast = useToast();

  const close = useAction(
    () =>
      api<{ closed: boolean; pendingVariances: number }>(`/receipts/${id}/close`, {
        method: 'POST',
        body: {},
      }),
    {
      invalidate: [['receipts'], ['inventory']],
      onSuccess: (res) => {
        setClosing(false);
        if (res.closed) toast.ok('Receipt closed and confirmed to the client');
        else
          toast.error(
            `${res.pendingVariances} variance(s) are outside tolerance and need approval`,
          );
      },
    },
  );
  const cancel = useAction(() => api(`/receipts/${id}/cancel`, { method: 'POST', body: {} }), {
    invalidate: [['receipts']],
    success: 'Receipt cancelled',
    onSuccess: () => setCancelling(false),
  });

  if (q.isLoading) return <Skeleton height={400} />;
  if (!r) return <ErrorMessage error={q.error} />;
  const open = ['arrived', 'receiving'].includes(r.status);
  const canReceive = can('inbound:receive') && open;
  const damaged = r.lineItems.reduce((a, l) => a + l.damagedQty, 0);
  const pending = r.variances.filter((v) => v.state === 'pending');

  return (
    <div className="stack-lg">
      <div>
        <Button
          variant="ghost"
          size="sm"
          icon={ArrowLeft}
          onClick={() => void navigate('/inbound/receipts')}
        >
          Receipts
        </Button>
      </div>
      <PageHeader
        title={`Receipt ${r.number}`}
        description={
          <span className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <ReceiptStatus status={r.status} />
            <span>{r.clientName}</span>
            {r.supplierName ? <span className="muted">from {r.supplierName}</span> : null}
            {r.poNumber ? (
              <span className="muted">
                {/^PO/i.test(r.poNumber) ? r.poNumber : `PO ${r.poNumber}`}
              </span>
            ) : null}
            <Badge>{r.type.toUpperCase()}</Badge>
          </span>
        }
        actions={
          <>
            {r.status === 'expected' && can('inbound:receive') ? (
              <Button variant="primary" icon={Truck} onClick={() => setCheckIn(true)}>
                Check in trailer
              </Button>
            ) : null}
            {canReceive ? (
              <Button
                variant="primary"
                icon={ClipboardCheck}
                loading={close.isPending}
                onClick={() => setClosing(true)}
              >
                Close receipt
              </Button>
            ) : null}
            {['expected', 'arrived'].includes(r.status) &&
            can('inbound:submit') &&
            !r.captures.length ? (
              <Button variant="danger" icon={XCircle} onClick={() => setCancelling(true)}>
                Cancel
              </Button>
            ) : null}
          </>
        }
      />
      <Card flush>
        <div className="card-body">
          <Milestones r={r} />
        </div>
      </Card>
      <div className="grid-stats">
        <Stat
          label="Received"
          icon={PackageCheck}
          tone="ok"
          value={formatNumber(r.receivedUnits)}
          foot={<Progress value={r.receivedUnits} total={r.expectedUnits} />}
        />
        <Stat
          label="Damaged"
          icon={XCircle}
          tone={damaged ? 'danger' : 'accent'}
          value={formatNumber(damaged)}
          foot={`${r.lineItems.length} lines`}
        />
        <Stat
          label="Putaway"
          icon={Forklift}
          tone={r.openPutaway ? 'warn' : 'ok'}
          value={`${r.putaway.filter((t) => t.state === 'done').length} / ${r.putaway.length}`}
          foot={r.openPutaway ? `${r.openPutaway} LPN(s) at the dock` : 'Nothing waiting'}
        />
        <Stat
          label="Dock-to-stock"
          icon={Timer}
          value={minutesLabel(r.dockToStockMinutes)}
          foot={r.arrivedAt ? `Arrived ${timeAgo(r.arrivedAt)}` : 'Not checked in'}
        />
      </div>
      {pending.length ? (
        <Callout tone="warn" title={`${pending.length} variance(s) outside the client's tolerance`}>
          Over {r.overTolerancePct}% / short {r.shortTolerancePct}% is accepted automatically. A
          supervisor must accept these before the receipt can close.
        </Callout>
      ) : null}
      {canReceive ? <ScanPanel receipt={r} onLabel={setLabel} /> : null}
      <Card title="Lines" sub="Expected against received, in base units" flush>
        <LinesTable r={r} canReceive={canReceive} onReceive={setReceiveLine} />
      </Card>
      {r.variances.length ? <Variances r={r} /> : null}
      <Card title="Putaway" sub="One task per LPN; the engine suggests a compatible location" flush>
        <PutawayTable
          tasks={r.putaway}
          onConfirm={can('putaway:execute') ? setPutaway : undefined}
          onLabel={(t) => setLabel(t.lpnId)}
        />
      </Card>
      <div className="grid-halves">
        <Card title="Trailer">
          <KeyValue
            items={[
              ['Door', r.doorCode ?? '—'],
              ['Carrier', r.carrier ?? '—'],
              ['Trailer', r.trailerNo ?? '—'],
              ['Seal', r.sealNo ?? '—'],
              ['Driver', r.driverName ?? '—'],
              [
                'Unload',
                r.unloadType
                  ? `${r.unloadType === 'floor' ? 'Floor-loaded' : 'Palletized'}${r.containerSize ? ` · ${r.containerSize}` : ''}`
                  : '—',
              ],
            ]}
          />
        </Card>
        <Card title="Receiving log" sub={`${r.captures.length} scan(s)`} flush>
          <CaptureLog r={r} onLabel={setLabel} />
        </Card>
      </div>
      {checkIn ? <CheckInDialog r={r} onClose={() => setCheckIn(false)} /> : null}
      {receiveLine ? (
        <ReceiveDialog
          r={r}
          line={receiveLine}
          onClose={() => setReceiveLine(null)}
          onLabel={setLabel}
        />
      ) : null}
      {label ? <LpnLabelDialog lpnId={label} onClose={() => setLabel(null)} /> : null}
      {putaway ? <PutawayDialog task={putaway} onClose={() => setPutaway(null)} /> : null}
      {closing ? (
        <ConfirmDialog
          title={`Close receipt ${r.number}?`}
          confirmLabel="Close receipt"
          pending={close.isPending}
          onConfirm={() => close.mutate(undefined)}
          onClose={() => setClosing(false)}
        >
          <div className="stack-sm">
            <span>
              {formatNumber(r.receivedUnits)} of {formatNumber(r.expectedUnits)} units received.
              Closing computes over/short/damage variances against the client&apos;s tolerances,
              confirms the receipt to the client (EDI 944 / webhook) and raises billing events.
            </span>
            {r.openPutaway ? (
              <span>
                Putaway can continue after closing; dock-to-stock stops at the last putaway.
              </span>
            ) : null}
          </div>
        </ConfirmDialog>
      ) : null}
      {cancelling ? (
        <ConfirmDialog
          title={`Cancel receipt ${r.number}?`}
          confirmLabel="Cancel receipt"
          danger
          pending={cancel.isPending}
          onConfirm={() => cancel.mutate(undefined)}
          onClose={() => setCancelling(false)}
        >
          Nothing has been received. The client is notified that the receipt was cancelled.
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

function LinesTable({
  r,
  canReceive,
  onReceive,
}: {
  r: ReceiptDetailDto;
  canReceive: boolean;
  onReceive: (l: ReceiptLineDto) => void;
}) {
  const columns: Column<ReceiptLineDto>[] = [
    {
      id: 'no',
      header: '#',
      width: 40,
      cell: (l) => <span className="num muted">{l.lineNo}</span>,
    },
    {
      id: 'item',
      header: 'Item',
      cell: (l) => (
        <div>
          <div className="primary-cell">{l.description}</div>
          <div className="sub">
            <span className="mono">{l.sku}</span>
            {l.unexpected ? (
              <>
                {' '}
                <Badge tone="warn">Not on ASN</Badge>
              </>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      id: 'expect',
      header: 'Expected lot / SSCC',
      cell: (l) => (
        <div className="small">
          {l.lot ? (
            <div>
              Lot {l.lot}
              {l.expiry ? ` · exp ${l.expiry}` : ''}
            </div>
          ) : null}
          {l.sscc ? <div className="mono muted">{l.sscc}</div> : null}
          {!l.lot && !l.sscc ? <span className="faint">—</span> : null}
        </div>
      ),
    },
    {
      id: 'controls',
      header: 'Capture',
      cell: (l) => (
        <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
          {l.controls.lot ? <Badge>Lot</Badge> : null}
          {l.controls.expiry ? <Badge>Expiry</Badge> : null}
          {l.controls.serial ? <Badge tone="info">Serial</Badge> : null}
          {l.controls.catchWeight ? <Badge>Weight</Badge> : null}
          {l.controls.origin ? <Badge>Origin</Badge> : null}
        </div>
      ),
    },
    {
      id: 'progress',
      header: 'Received',
      cell: (l) => <Progress value={l.receivedQty + l.damagedQty} total={l.expectedQty} />,
    },
    {
      id: 'damaged',
      header: 'Damaged',
      align: 'right',
      cell: (l) =>
        l.damagedQty ? (
          <Badge tone="danger">{formatNumber(l.damagedQty)}</Badge>
        ) : (
          <span className="faint">0</span>
        ),
    },
    ...(canReceive
      ? [
          {
            id: 'act',
            header: '',
            align: 'right' as const,
            cell: (l: ReceiptLineDto) => (
              <Button size="sm" icon={ScanLine} onClick={() => onReceive(l)}>
                Receive
              </Button>
            ),
          },
        ]
      : []),
  ];
  return (
    <DataTable
      columns={columns}
      rows={r.lineItems}
      rowKey={(l) => l.id}
      pageSize={100}
      empty={
        <EmptyState icon={Truck} title="No lines yet">
          Blind receipt: scan items as they come off the trailer.
        </EmptyState>
      }
    />
  );
}

/** Fast path for the dock: scan a GS1/GTIN/SKU/SSCC barcode and press Enter. */
function ScanPanel({
  receipt,
  onLabel,
}: {
  receipt: ReceiptDetailDto;
  onLabel: (lpnId: string) => void;
}) {
  const [barcode, setBarcode] = useState('');
  const [qty, setQty] = useState('');
  const [lpn, setLpn] = useState('');
  const [last, setLast] = useState<ReceiveResult | null>(null);
  const ref = useRef<HTMLInputElement>(null);
  const toast = useToast();
  useEffect(() => ref.current?.focus(), []);
  const receive = useAction(
    () =>
      api<ReceiveResult>(`/receipts/${receipt.id}/receive`, {
        method: 'POST',
        body: {
          barcode: barcode.trim(),
          qty: qty ? Number(qty) : undefined,
          lpnCode: lpn.trim() || undefined,
        },
      }),
    {
      invalidate: [['receipts']],
      onSuccess: (res) => {
        setLast(res);
        toast.ok(`Received ${res.qty} on ${res.lpnCode}`);
        setBarcode('');
        setQty('');
        ref.current?.focus();
      },
    },
  );
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (barcode.trim()) receive.mutate(undefined);
  };
  return (
    <Card
      title="Scan to receive"
      sub="GS1-128 (GTIN, lot, expiry, serial, count), a GTIN of any pack level, a SKU, or a pallet SSCC. Items that need more data open the line's receive form."
    >
      <form
        onSubmit={submit}
        className="row"
        style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}
      >
        <Field label="Barcode" className="grow">
          <input
            ref={ref}
            className="input mono"
            placeholder="Scan…"
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            autoComplete="off"
            style={{ minWidth: 280 }}
          />
        </Field>
        <Field label="Qty" hint="Blank = 1 or the scanned count">
          <input
            className="input num"
            style={{ width: 90 }}
            inputMode="numeric"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
        </Field>
        <Field label="Onto LPN" hint="Blank = new pallet">
          <input
            className="input mono"
            style={{ width: 170 }}
            value={lpn}
            onChange={(e) => setLpn(e.target.value.toUpperCase())}
          />
        </Field>
        <Button
          type="submit"
          variant="primary"
          icon={ScanLine}
          loading={receive.isPending}
          disabled={!barcode.trim()}
        >
          Receive
        </Button>
      </form>
      {last ? (
        <div className="callout callout-ok" style={{ marginTop: 14 }}>
          <CheckCircle2 aria-hidden />
          <div className="row" style={{ gap: 12, flexWrap: 'wrap', flex: 1 }}>
            <span>
              {formatNumber(last.qty)} units on <span className="mono">{last.lpnCode}</span>
              {last.suggestedLocation ? (
                <>
                  {' '}
                  → put away to <strong className="mono">{last.suggestedLocation.code}</strong>{' '}
                  <span className="muted">({last.suggestedLocation.rule})</span>
                </>
              ) : (
                <span className="warn-text">
                  {' '}
                  — no compatible location free; a supervisor will direct it
                </span>
              )}
            </span>
            <span className="spacer" />
            <Button size="sm" icon={Printer} onClick={() => onLabel(last.lpnId)}>
              Label
            </Button>
            <Button size="sm" onClick={() => setLpn(last.lpnCode)}>
              Keep building this pallet
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

function PhotoPicker({
  value,
  onChange,
  clientId,
}: {
  value: AttachmentDto[];
  onChange: (v: AttachmentDto[]) => void;
  clientId: string;
}) {
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const out: AttachmentDto[] = [];
      for (const f of Array.from(files).slice(0, 5)) {
        if (f.size > 5 * 1024 * 1024) throw new Error(`${f.name} is larger than 5 MB`);
        out.push(
          await api<AttachmentDto>('/attachments', {
            method: 'POST',
            body: { filename: f.name, contentType: f.type, data: await fileToBase64(f), clientId },
          }),
        );
      }
      onChange([...value, ...out]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
      {value.map((a) => (
        <Badge key={a.id} tone="info">
          {a.filename}
        </Badge>
      ))}
      <label className="btn btn-sm">
        {busy ? (
          <span className="spinner" style={{ width: 14, height: 14 }} />
        ) : (
          <Camera aria-hidden />
        )}
        Add photo
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          multiple
          hidden
          onChange={(e) => void upload(e.target.files)}
        />
      </label>
    </div>
  );
}

function ReceiveDialog({
  r,
  line,
  onClose,
  onLabel,
}: {
  r: ReceiptDetailDto;
  line: ReceiptLineDto;
  onClose: () => void;
  onLabel: (id: string) => void;
}) {
  const remaining = Math.max(0, line.expectedQty - line.receivedQty - line.damagedQty);
  const [qty, setQty] = useState(String(line.controls.serial ? '' : remaining || ''));
  const [lot, setLot] = useState(line.lot ?? '');
  const [expiry, setExpiry] = useState(line.expiry ?? '');
  const [serials, setSerials] = useState('');
  const [weight, setWeight] = useState('');
  const [origin, setOrigin] = useState('');
  const [condition, setCondition] = useState<'good' | 'damaged'>('good');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [photos, setPhotos] = useState<AttachmentDto[]>([]);
  const [lpn, setLpn] = useState('');
  const serialList = serials
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const receive = useAction(
    () =>
      api<ReceiveResult>(`/receipts/${r.id}/receive`, {
        method: 'POST',
        body: {
          lineId: line.id,
          qty: line.controls.serial ? undefined : Number(qty),
          lot: lot || null,
          expiry: expiry || null,
          serials: line.controls.serial ? serialList : undefined,
          catchWeightG: weight ? Math.round(Number(weight) * 1000) : null,
          countryOfOrigin: origin || null,
          condition,
          reasonCodeId: condition === 'damaged' ? reason : null,
          note: note || null,
          attachmentIds: photos.map((p) => p.id),
          lpnCode: lpn.trim() || undefined,
        },
      }),
    {
      invalidate: [['receipts'], ['inventory']],
      success: (res) =>
        `Received ${res.qty} × ${line.sku} on ${res.lpnCode}${res.suggestedLocation ? ` → ${res.suggestedLocation.code}` : ''}`,
      onSuccess: (res) => {
        onClose();
        onLabel(res.lpnId);
      },
    },
  );
  const ready =
    (line.controls.serial ? serialList.length > 0 : Number(qty) > 0) &&
    (!line.controls.lot || lot) &&
    (!line.controls.expiry || expiry) &&
    (!line.controls.catchWeight || weight) &&
    (!line.controls.origin || origin.length === 2) &&
    (condition === 'good' || reason);
  return (
    <Dialog
      wide
      title={`Receive ${line.sku}`}
      description={`${line.description} · ${formatNumber(remaining)} of ${formatNumber(line.expectedQty)} still expected`}
      onClose={onClose}
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) receive.mutate(undefined);
      }}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="primary"
            icon={ScanLine}
            loading={receive.isPending}
            disabled={!ready}
          >
            Receive
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="segmented" role="radiogroup" aria-label="Condition">
          <button
            type="button"
            role="radio"
            aria-checked={condition === 'good'}
            className={condition === 'good' ? 'active' : ''}
            onClick={() => setCondition('good')}
          >
            Good stock
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={condition === 'damaged'}
            className={condition === 'damaged' ? 'active' : ''}
            onClick={() => setCondition('damaged')}
          >
            Damaged
          </button>
        </div>
        <div className="form-grid-3">
          {line.controls.serial ? (
            <Field
              label="Serial numbers"
              hint={`${serialList.length} scanned — one per unit`}
              className="span-3"
            >
              <textarea
                className="textarea mono"
                rows={3}
                value={serials}
                onChange={(e) => setSerials(e.target.value)}
                placeholder="Scan each serial"
              />
            </Field>
          ) : (
            <Field label={`Quantity (${line.uom})`}>
              <input
                className="input num"
                inputMode="numeric"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
              />
            </Field>
          )}
          {line.controls.lot || line.lot ? (
            <Field label="Lot">
              <input className="input mono" value={lot} onChange={(e) => setLot(e.target.value)} />
            </Field>
          ) : null}
          {line.controls.expiry || line.expiry ? (
            <Field label="Expiry">
              <input
                className="input"
                type="date"
                value={expiry}
                onChange={(e) => setExpiry(e.target.value)}
              />
            </Field>
          ) : null}
          {line.controls.catchWeight ? (
            <Field label="Catch weight (kg)">
              <input
                className="input num"
                inputMode="decimal"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
              />
            </Field>
          ) : null}
          {line.controls.origin ? (
            <Field label="Country of origin" hint="Two letters, e.g. US, MX">
              <input
                className="input mono"
                maxLength={2}
                value={origin}
                onChange={(e) => setOrigin(e.target.value.toUpperCase())}
              />
            </Field>
          ) : null}
          <Field label="Onto LPN" hint="Blank = new pallet label">
            <input
              className="input mono"
              value={lpn}
              onChange={(e) => setLpn(e.target.value.toUpperCase())}
            />
          </Field>
        </div>
        {condition === 'damaged' ? (
          <div className="form-grid">
            <Field label="Damage reason">
              <ReasonSelect categories={['damage']} value={reason} onChange={setReason} />
            </Field>
            <Field label="Note">
              <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
            <Field
              label="Photos"
              className="span-2"
              hint="Evidence for the carrier claim (JPEG/PNG/WebP, up to 5 MB each)"
            >
              <PhotoPicker value={photos} onChange={setPhotos} clientId={r.clientId} />
            </Field>
          </div>
        ) : null}
        {condition === 'damaged' ? (
          <Callout tone="warn">
            Damaged units are received into the Damaged status and cannot be allocated to orders.
          </Callout>
        ) : null}
      </div>
    </Dialog>
  );
}

function Variances({ r }: { r: ReceiptDetailDto }) {
  const { can } = useAuth();
  const [busy, setBusy] = useState<string | null>(null);
  const accept = useAction(
    (v: { id: string }) =>
      api(`/receipts/${r.id}/variances/${v.id}/accept`, { method: 'POST', body: {} }),
    { invalidate: [['receipts']], success: 'Variance accepted', onSuccess: () => setBusy(null) },
  );
  const label = {
    over: 'Over',
    short: 'Short',
    damaged: 'Damaged',
    unexpected: 'Not on ASN',
  } as const;
  return (
    <Card
      title="Variances"
      sub={`Tolerance: over ${r.overTolerancePct}% · short ${r.shortTolerancePct}%`}
      flush
    >
      <div className="list">
        {r.variances.map((v) => (
          <div key={v.id} className="list-item">
            <span
              className={`icon-tile ${v.state === 'pending' ? 'warn' : 'ok'}`}
              style={{ width: 32, height: 32 }}
            >
              {v.state === 'pending' ? <Tag aria-hidden /> : <CheckCircle2 aria-hidden />}
            </span>
            <div className="grow">
              <div className="title">
                {label[v.type]} {formatNumber(v.qty)} × <span className="mono">{v.sku}</span>
              </div>
              <div className="muted small">
                Expected {formatNumber(v.expectedQty)}, counted {formatNumber(v.actualQty)} ·{' '}
                {v.withinTolerance
                  ? 'within tolerance'
                  : v.state === 'accepted'
                    ? `accepted by ${v.decidedBy ?? '—'} ${timeAgo(v.decidedAt)}${v.note ? ` — ${v.note}` : ''}`
                    : 'outside tolerance'}
              </div>
            </div>
            {v.state === 'pending' && can('inbound:approve') ? (
              <Button
                size="sm"
                variant="primary"
                loading={busy === v.id && accept.isPending}
                onClick={() => {
                  setBusy(v.id);
                  accept.mutate({ id: v.id });
                }}
              >
                Accept
              </Button>
            ) : (
              <Badge tone={v.state === 'pending' ? 'warn' : 'ok'}>
                {v.state === 'pending' ? 'Needs approval' : 'Accepted'}
              </Badge>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

export function PutawayTable({
  tasks,
  onConfirm,
  onLabel,
}: {
  tasks: PutawayTaskDto[];
  onConfirm?: (t: PutawayTaskDto) => void;
  onLabel?: (t: PutawayTaskDto) => void;
}) {
  const columns: Column<PutawayTaskDto>[] = [
    { id: 'lpn', header: 'LPN', cell: (t) => <span className="mono">{t.lpnCode}</span> },
    {
      id: 'item',
      header: 'Contents',
      cell: (t) => (
        <div>
          <div className="primary-cell">
            <span className="mono">{t.sku}</span> × {formatNumber(t.qty)}
          </div>
          <div className="sub">
            {t.items > 1 ? `${t.items} SKUs (mixed pallet)` : t.description}
          </div>
        </div>
      ),
    },
    { id: 'from', header: 'From', cell: (t) => <span className="mono">{t.fromLocationCode}</span> },
    {
      id: 'to',
      header: 'Suggested',
      cell: (t) =>
        t.state === 'done' ? (
          <div>
            <span className="mono">{t.toLocationCode}</span>{' '}
            {t.overridden ? (
              <Badge tone="warn">Override</Badge>
            ) : (
              <Badge tone="ok">As directed</Badge>
            )}
          </div>
        ) : t.suggestedLocationCode ? (
          <div>
            <span className="mono">{t.suggestedLocationCode}</span>
            <div className="sub">{t.rule}</div>
          </div>
        ) : (
          <Badge tone="danger">No compatible location</Badge>
        ),
    },
    {
      id: 'when',
      header: 'Status',
      cell: (t) =>
        t.state === 'done' ? (
          <div className="small">
            Done {timeAgo(t.completedAt)}
            <div className="sub">{t.completedBy}</div>
          </div>
        ) : (
          <span className="small muted">Waiting {timeAgo(t.createdAt).replace(' ago', '')}</span>
        ),
    },
    {
      id: 'act',
      header: '',
      align: 'right',
      cell: (t) => (
        <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
          {onLabel ? (
            <Button
              size="sm"
              variant="ghost"
              icon={Printer}
              aria-label="Print label"
              onClick={() => onLabel(t)}
            />
          ) : null}
          {t.state === 'open' && onConfirm ? (
            <Button size="sm" variant="primary" icon={Forklift} onClick={() => onConfirm(t)}>
              Put away
            </Button>
          ) : null}
        </div>
      ),
    },
  ];
  return (
    <DataTable
      columns={columns}
      rows={tasks}
      rowKey={(t) => t.id}
      pageSize={50}
      empty={
        <EmptyState icon={Forklift} title="Nothing to put away">
          Received LPNs appear here with a suggested location.
        </EmptyState>
      }
    />
  );
}

export function PutawayDialog({ task, onClose }: { task: PutawayTaskDto; onClose: () => void }) {
  const [t, setT] = useState(task);
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const differs = code.trim() !== '' && code.trim().toUpperCase() !== t.suggestedLocationCode;
  const confirm = useAction(
    () =>
      api<PutawayTaskDto>(`/putaway-tasks/${t.id}/confirm`, {
        method: 'POST',
        body: {
          locationCode: code.trim().toUpperCase(),
          reasonCodeId: differs ? reason : null,
          note: note || null,
        },
      }),
    {
      invalidate: [['receipts'], ['putaway'], ['inventory']],
      success: (res) => `${res.lpnCode} put away to ${res.toLocationCode}`,
      onSuccess: onClose,
    },
  );
  const again = useAction(
    () => api<PutawayTaskDto>(`/putaway-tasks/${t.id}/suggest`, { method: 'POST' }),
    {
      invalidate: [['putaway'], ['receipts']],
      onSuccess: setT,
    },
  );
  return (
    <Dialog
      title={`Put away ${t.lpnCode}`}
      description={`${t.sku} × ${formatNumber(t.qty)}${t.items > 1 ? ` (+${t.items - 1} SKUs)` : ''} from ${t.fromLocationCode}`}
      onClose={onClose}
      onSubmit={(e) => {
        e.preventDefault();
        if (code.trim() && (!differs || reason)) confirm.mutate(undefined);
      }}
      footer={
        <>
          <Button onClick={() => again.mutate(undefined)} loading={again.isPending}>
            Suggest again
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={confirm.isPending}
            disabled={!code.trim() || (differs && !reason)}
          >
            Confirm putaway
          </Button>
        </>
      }
    >
      <div className="stack">
        {t.suggestedLocationCode ? (
          <div className="putaway-target">
            <div className="muted small">Go to</div>
            <div className="putaway-code mono">{t.suggestedLocationCode}</div>
            <div className="muted small">{t.rule}</div>
          </div>
        ) : (
          <Callout tone="warn" title="No compatible location is free">
            Every candidate failed a placement rule (temperature, hazmat, client, mixing or
            capacity). Choose a location; it still has to pass the rules.
          </Callout>
        )}
        <Field
          label="Scan the location"
          hint="Placement rules are checked again on confirm and cannot be overridden"
        >
          <input
            className="input mono"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder={t.suggestedLocationCode ?? 'Location code'}
          />
        </Field>
        {differs ? (
          <div className="form-grid">
            <Field label="Override reason">
              <ReasonSelect categories={['override']} value={reason} onChange={setReason} />
            </Field>
            <Field label="Note">
              <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

function CaptureLog({ r, onLabel }: { r: ReceiptDetailDto; onLabel: (id: string) => void }) {
  const [photo, setPhoto] = useState<string | null>(null);
  if (!r.captures.length) return <div className="card-body muted small">Nothing received yet.</div>;
  return (
    <>
      <div className="list" style={{ maxHeight: 360, overflow: 'auto' }}>
        {r.captures.slice(0, 200).map((c) => (
          <div key={c.id} className="list-item">
            <span
              className={`icon-tile ${c.condition === 'damaged' ? 'danger' : 'ok'}`}
              style={{ width: 28, height: 28 }}
            >
              {c.condition === 'damaged' ? <XCircle aria-hidden /> : <PackageCheck aria-hidden />}
            </span>
            <div className="grow">
              <div className="title">
                {formatNumber(c.qty)} × <span className="mono">{c.sku}</span>
                {c.lot ? <span className="muted"> · lot {c.lot}</span> : null}
                {c.serial ? <span className="muted"> · S/N {c.serial}</span> : null}
              </div>
              <div className="muted small">
                {c.lpnCode ? <span className="mono">{c.lpnCode}</span> : null} · {c.userName ?? '—'}{' '}
                · {timeAgo(c.createdAt)}
                {c.reason ? ` · ${c.reason}` : ''}
              </div>
            </div>
            {c.attachmentIds.map((a) => (
              <Button
                key={a}
                size="sm"
                variant="ghost"
                icon={Camera}
                aria-label="View photo"
                onClick={() => setPhoto(a)}
              />
            ))}
            {c.lpnId ? (
              <Button
                size="sm"
                variant="ghost"
                icon={Printer}
                aria-label="Print label"
                onClick={() => onLabel(c.lpnId!)}
              />
            ) : null}
          </div>
        ))}
      </div>
      {photo ? <PhotoViewer id={photo} onClose={() => setPhoto(null)} /> : null}
    </>
  );
}

function PhotoViewer({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['attachment', id],
    queryFn: () => fetchObjectUrl(`/attachments/${id}`),
    staleTime: Infinity,
  });
  return (
    <Dialog title="Photo" onClose={onClose} wide>
      {q.data ? (
        <img src={q.data} alt="Receiving evidence" style={{ maxWidth: '100%', borderRadius: 8 }} />
      ) : (
        <Skeleton height={300} />
      )}
    </Dialog>
  );
}

function CheckInDialog({ r, onClose }: { r: ReceiptDetailDto; onClose: () => void }) {
  const doors = useQuery({
    queryKey: ['locations', 'docks', r.siteId],
    queryFn: () => api<Page<LocationDto>>(`/locations?siteId=${r.siteId}&type=dock&limit=100`),
  });
  const [door, setDoor] = useState('');
  const [carrier, setCarrier] = useState(r.carrier ?? '');
  const [trailer, setTrailer] = useState('');
  const [seal, setSeal] = useState('');
  const [driver, setDriver] = useState('');
  const [unload, setUnload] = useState<'palletized' | 'floor'>('palletized');
  const [size, setSize] = useState('53ft');
  const checkIn = useAction(
    () =>
      api(`/receipts/${r.id}/check-in`, {
        method: 'POST',
        body: {
          doorLocationId: door,
          carrier: carrier || null,
          trailerNo: trailer || null,
          sealNo: seal || null,
          driverName: driver || null,
          unloadType: unload,
          containerSize: size || null,
        },
      }),
    {
      invalidate: [['receipts']],
      success: 'Trailer checked in — dock-to-stock clock started',
      onSuccess: onClose,
    },
  );
  return (
    <Dialog
      title="Check in trailer"
      description="Records arrival, door and seal. The dock-to-stock clock starts now."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            icon={Truck}
            loading={checkIn.isPending}
            disabled={!door}
            onClick={() => checkIn.mutate(undefined)}
          >
            Check in
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Door">
          <select className="select" value={door} onChange={(e) => setDoor(e.target.value)}>
            <option value="">Choose a door…</option>
            {(doors.data?.items ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.code}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Carrier">
          <input className="input" value={carrier} onChange={(e) => setCarrier(e.target.value)} />
        </Field>
        <Field label="Trailer / container">
          <input
            className="input mono"
            value={trailer}
            onChange={(e) => setTrailer(e.target.value.toUpperCase())}
          />
        </Field>
        <Field label="Seal number">
          <input
            className="input mono"
            value={seal}
            onChange={(e) => setSeal(e.target.value.toUpperCase())}
          />
        </Field>
        <Field label="Driver">
          <input className="input" value={driver} onChange={(e) => setDriver(e.target.value)} />
        </Field>
        <Field label="Unload">
          <select
            className="select"
            value={unload}
            onChange={(e) => setUnload(e.target.value as 'palletized' | 'floor')}
          >
            <option value="palletized">Palletized</option>
            <option value="floor">Floor-loaded (billable labor)</option>
          </select>
        </Field>
        <Field label="Equipment">
          <select className="select" value={size} onChange={(e) => setSize(e.target.value)}>
            {['53ft', '40ft', '45ft', '20ft', 'box_truck', 'parcel'].map((s) => (
              <option key={s} value={s}>
                {s.replace('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </Dialog>
  );
}
